import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useCurrentInternalUser, isAdmin } from '../lib/currentUser';
import Sidebar from '../components/Sidebar';
import BriefReporting from '../components/BriefReporting';
import SuppliersReporting from '../components/SuppliersReporting';
import ReportingAccessDenied from '../components/ReportingAccessDenied';
import { OttoLoading } from '../components/otto';

// Top-level Reporting surface. Previously the Brief and Supplier
// reporting views lived as tabs inside /briefs and /suppliers
// respectively — that worked but meant the two analytical views were
// reached via list pages instead of as analytics in their own right.
// Reporting is now a System nav item; the two existing components
// (BriefReporting, SuppliersReporting) mount unchanged here, with
// sub-tabs to switch between them.
//
// Sub-tab is URL-driven via ?tab=briefs|suppliers so links from the
// "Back to Reporting" affordance on /briefs and /suppliers list
// views can land on the right view. Default sub-tab: briefs.
//
// Data fetches mirror the SELECTs used by /briefs and /suppliers
// today — these are loaded once on mount; drills navigate away to
// the list pages, which re-fetch.

const tabBase = "font-['Metropolis'] text-xs font-bold tracking-[0.2em] uppercase pb-3 transition-colors inline-flex items-center gap-2";
// One container for the whole page (reporting-aligned §1) — header, tabs,
// controls and both tab bodies share these left/right edges. Capped at 1240px,
// centred, 28px side padding. Nothing else on the page sets its own width.
const container = "max-w-[1240px] mx-auto px-7";

// Page-level timeframe — one control in the header drives BOTH tabs (it used
// to be a per-tab toolbar, and Suppliers' copy was inert). Canonical values are
// uppercase; the Suppliers tab maps them to its own lowercase vocabulary at the
// boundary. Briefs already speaks this set natively.
const TIMEFRAMES = [
  { value: '7D',  label: '7D'  },
  { value: '30D', label: '30D' },
  { value: '90D', label: '90D' },
  { value: 'YTD', label: 'YTD' },
  { value: 'All', label: 'All' },
];

export default function Reporting() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user: currentInternalUser } = useCurrentInternalUser();
  const [briefs, setBriefs] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);

  const initialTab = searchParams.get('tab') === 'suppliers' ? 'suppliers' : 'briefs';
  const [subTab, setSubTab] = useState(initialTab);

  // Shared timeframe. Smart default (kept from the old Briefs toolbar, now
  // page-wide): open on 90D when there's data older than 90 days, otherwise All
  // — the control adds nothing when everything already sits inside 90D. Flips to
  // user control the moment they pick a value. Uses briefs as the density signal.
  const [timeframe, setTimeframe] = useState('90D');
  const [timeframeUserSet, setTimeframeUserSet] = useState(false);
  useEffect(() => {
    if (timeframeUserSet || loading || briefs.length === 0) return;
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 90);
    const anyOlder = briefs.some(b => b.created_at && new Date(b.created_at) < cutoff);
    setTimeframe(anyOlder ? '90D' : 'All');
  }, [briefs, timeframeUserSet, loading]);
  const onTimeframeChange = (tf) => { setTimeframeUserSet(true); setTimeframe(tf); };

  const setSubTabSticky = (key) => {
    setSubTab(key);
    const next = new URLSearchParams(searchParams);
    next.set('tab', key);
    setSearchParams(next, { replace: true });
  };

  // Arrow-key navigation across the tablist (§6). Left/Right roves selection and
  // moves focus to the newly-selected tab.
  const onTabKeyDown = (e) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const next = subTab === 'briefs' ? 'suppliers' : 'briefs';
    setSubTabSticky(next);
    requestAnimationFrame(() => document.getElementById(`reporting-tab-${next}`)?.focus());
  };

  useEffect(() => {
    // Mirror the briefs SELECT from /briefs and the suppliers
    // assembly from /suppliers so the embedded reporting components
    // get the same data shape they did before. Slight duplication
    // accepted in exchange for keeping the two list pages and this
    // page independently fetchable; the alternative (a shared hook)
    // would couple their loading states unnecessarily.
    let cancelled = false;
    (async () => {
      const [
        briefsRes,
        suppliersRes,
        recipientsRes,
        responsesRes,
        awardedBriefsRes,
      ] = await Promise.all([
        supabase
          .from('briefs')
          .select('id, brief_name, fusion_project_name, fusion_project_number, brief_category, status, created_at, closed_at, required_response_time, is_privileged, archived_at, owner_id, awarded_supplier_id, awarded_amount, owner:internal_users!owner_id(id, full_name, job_title, avatar_id, avatar_url), brief_owners(user_id, internal_users!user_id(id, full_name, job_title, avatar_id, avatar_url)), brief_responses(count), brief_recipients(supplier_id)')
          .order('created_at', { ascending: false }),
        supabase.from('suppliers').select('*').order('supplier_name'),
        supabase.from('brief_recipients').select('supplier_id, notified_at'),
        supabase.from('brief_responses').select('supplier_id, submitted_at').eq('is_submitted', true),
        supabase.from('briefs').select('awarded_supplier_id, awarded_amount').eq('status', 'closed').not('awarded_supplier_id', 'is', null),
      ]);
      if (cancelled) return;

      setBriefs((briefsRes.data || []).map(b => ({
        ...b,
        owners_list: (b.brief_owners || []).map(bo => bo.internal_users).filter(Boolean),
        response_count: b.brief_responses?.[0]?.count || 0,
      })));

      // Per-supplier aggregates — see src/pages/Suppliers.js for the
      // original derivation. Kept in sync deliberately.
      const responseCounts = new Map();
      for (const r of responsesRes.data || []) {
        responseCounts.set(r.supplier_id, (responseCounts.get(r.supplier_id) || 0) + 1);
      }
      const lastActivity = new Map();
      const recordActivity = (supplierId, iso) => {
        if (!supplierId || !iso) return;
        const prev = lastActivity.get(supplierId);
        if (!prev || new Date(iso) > new Date(prev)) lastActivity.set(supplierId, iso);
      };
      const cutoff90 = Date.now() - 90 * 86400000;
      const invitedCount90 = new Map();
      for (const r of recipientsRes.data || []) {
        recordActivity(r.supplier_id, r.notified_at);
        if (r.supplier_id && r.notified_at && new Date(r.notified_at).getTime() >= cutoff90) {
          invitedCount90.set(r.supplier_id, (invitedCount90.get(r.supplier_id) || 0) + 1);
        }
      }
      for (const r of responsesRes.data || []) recordActivity(r.supplier_id, r.submitted_at);

      const awardedCount = new Map();
      const awardedTotal = new Map();
      for (const b of awardedBriefsRes.data || []) {
        if (!b.awarded_supplier_id) continue;
        awardedCount.set(b.awarded_supplier_id, (awardedCount.get(b.awarded_supplier_id) || 0) + 1);
        const amt = Number(b.awarded_amount);
        if (Number.isFinite(amt) && amt > 0) {
          awardedTotal.set(b.awarded_supplier_id, (awardedTotal.get(b.awarded_supplier_id) || 0) + amt);
        }
      }

      setSuppliers((suppliersRes.data || []).map(s => ({
        ...s,
        response_count: responseCounts.get(s.id) || 0,
        last_activity_at: lastActivity.get(s.id) || null,
        awarded_count: awardedCount.get(s.id) || 0,
        awarded_total: awardedTotal.get(s.id) || 0,
        invited_count_90d: invitedCount90.get(s.id) || 0,
      })));

      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  // Same drill contract Briefs.js used when the report lived inside
  // it. Every drill leaves /reporting and lands on a list view with
  // ?ref=reporting so the destination renders its Back-to-Reporting
  // affordance.
  const handleBriefDrillDown = (payload) => {
    if (!payload) return;
    if (payload.kind === 'supplier') {
      if (payload.supplierId) navigate(`/suppliers/${payload.supplierId}`);
      return;
    }
    if (payload.kind === 'all_suppliers') {
      navigate('/suppliers?ref=reporting');
      return;
    }
    if (payload.kind === 'suppliers_filtered') {
      const sd = payload.suppliersDrill || {};
      const params = new URLSearchParams();
      Object.entries(sd.params || {}).forEach(([k, v]) => params.set(k, v));
      params.set('ref', 'reporting');
      const url = `/suppliers?${params.toString()}`;
      navigate(url, { state: { reportingDrill: { idAllowlist: sd.idAllowlist, banner: sd.banner } } });
      return;
    }
    // Default: same-table drill on /briefs.
    const params = new URLSearchParams();
    Object.entries(payload.params || {}).forEach(([k, v]) => params.set(k, v));
    params.set('ref', 'reporting');
    navigate(`/briefs?${params.toString()}`, { state: { reportingDrill: payload.drill } });
  };

  // Admin gate — same posture as the inline tab had before. Non-admin
  // internal users hit the existing access-denied surface.
  if (!isAdmin(currentInternalUser)) {
    return (
      <div className="min-h-screen bg-canvas flex">
        <Sidebar currentInternalUser={currentInternalUser} />
        <div className="flex-1 flex flex-col">
          <div className="pt-8">
            <div className="px-8 mb-6">
              <h1 className="fusion-h1 text-fusion-forest">Reporting</h1>
            </div>
            <div className="mx-8 h-px bg-[#E6E9DA]" aria-hidden="true" />
          </div>
          <div className="flex-1 p-8">
            <ReportingAccessDenied />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-canvas flex">
      <Sidebar currentInternalUser={currentInternalUser} />

      <div className="flex-1 flex flex-col">
        <div className="pt-8">
          <div className={`${container} mb-5`}>
            <h1 className="fusion-h1 text-fusion-forest">Reporting</h1>
            <p className="font-['Metropolis'] text-sm text-fusion-mid-grey mt-2">Briefs and suppliers analytics, side by side.</p>
            {/* Admin-view pill — describes the whole page's data scope, so it
                renders once here rather than inside a tab (reporting-aligned §2). */}
            <div className="mt-3 inline-flex items-center gap-2 bg-[#92D400]/[0.06] border border-[#92D400]/30 rounded-full px-3 py-1.5">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#92D400" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
              <span className="font-['Metropolis'] text-[10px] font-bold tracking-[2px] uppercase text-fusion-forest">Admin View</span>
              <span className="font-['Metropolis'] text-[11px] text-fusion-mid-grey">— data covers all owners, projects and suppliers</span>
            </div>
          </div>
          <div className={`${container} flex items-center justify-between gap-8 border-b border-line`}>
            <div className="flex items-center gap-8" role="tablist" aria-label="Reporting sections" onKeyDown={onTabKeyDown}>
            <button
              type="button"
              id="reporting-tab-briefs"
              role="tab"
              aria-selected={subTab === 'briefs'}
              aria-controls="reporting-panel"
              tabIndex={subTab === 'briefs' ? 0 : -1}
              onClick={() => setSubTabSticky('briefs')}
              className={`${tabBase} ${subTab === 'briefs' ? 'text-fusion-forest border-b-2 border-[#92D400]' : 'text-[#51534A] hover:text-fusion-mid-grey'}`}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="18" y1="20" x2="18" y2="10" />
                <line x1="12" y1="20" x2="12" y2="4" />
                <line x1="6"  y1="20" x2="6"  y2="14" />
                <line x1="3"  y1="20" x2="21" y2="20" />
              </svg>
              Briefs
            </button>
            <button
              type="button"
              id="reporting-tab-suppliers"
              role="tab"
              aria-selected={subTab === 'suppliers'}
              aria-controls="reporting-panel"
              tabIndex={subTab === 'suppliers' ? 0 : -1}
              onClick={() => setSubTabSticky('suppliers')}
              className={`${tabBase} ${subTab === 'suppliers' ? 'text-fusion-forest border-b-2 border-[#92D400]' : 'text-[#51534A] hover:text-fusion-mid-grey'}`}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 00-3-3.87" />
                <path d="M16 3.13a4 4 0 010 7.75" />
              </svg>
              Suppliers
            </button>
            </div>
            {/* Shared timeframe — one control for whichever tab is open. */}
            <div className="hidden sm:flex items-center gap-2 pb-3">
              <span className="font-['Metropolis'] text-[10px] font-bold tracking-[0.2em] uppercase text-[#51534A]">Timeframe</span>
              <div className="inline-flex items-center bg-white border border-line rounded-xl p-1 gap-0.5" role="group" aria-label="Timeframe">
                {TIMEFRAMES.map(tf => (
                  <button
                    key={tf.value}
                    type="button"
                    aria-pressed={timeframe === tf.value}
                    onClick={() => onTimeframeChange(tf.value)}
                    className={`px-3 py-1.5 rounded-lg text-[11px] font-bold tracking-[2px] uppercase transition-colors ${
                      timeframe === tf.value
                        ? 'bg-[#92D400]/15 text-fusion-forest'
                        : 'text-[#51534A] hover:text-ink'
                    }`}>
                    {tf.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-auto">
          <div
            className={`${container} py-8 pb-14`}
            id="reporting-panel"
            role="tabpanel"
            aria-labelledby={`reporting-tab-${subTab}`}
            tabIndex={0}>
            {loading ? (
              <div className="py-20"><OttoLoading size="medium" message="Loading reporting" /></div>
            ) : subTab === 'briefs' ? (
              <BriefReporting
                briefs={briefs}
                currentInternalUser={currentInternalUser}
                timeframe={timeframe}
                onDrillDown={handleBriefDrillDown} />
            ) : (
              <SuppliersReporting suppliers={suppliers} timeframe={timeframe} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
